// Arena website-template options (DIRECTION.md §10, founder-approved).
//
// League-wide: one website template and one set of options for every sport.
// Only `canvas: 'auto'` can resolve differently per sport (it follows the
// active sport theme's background tone). This array MUST deep-equal the
// backend spec in backend/src/services/site-templates/templates/arena.ts
// (options.sync test, which compares the English text). Options change layout
// or emphasis, never colours; colours come from the brand pipeline
// (lib/brand.ts). The text is admin-facing: selectors on the admin catalogue
// (siteTemplates.arena), shown in the admin's language (#1063).

import type { OptionsOf, OptionsSchema } from '../../core/template/options'

export const ARENA_OPTIONS = [
  {
    key: 'scoresStrip',
    type: 'enum',
    label: (m) => m.siteTemplates.arena.options.scoresStrip.label,
    help: (m) => m.siteTemplates.arena.options.scoresStrip.help,
    default: 'on',
    values: [
      { value: 'on', label: (m) => m.siteTemplates.arena.options.scoresStrip.on },
      { value: 'gameDays', label: (m) => m.siteTemplates.arena.options.scoresStrip.gameDays },
      { value: 'off', label: (m) => m.siteTemplates.arena.options.scoresStrip.off },
    ],
  },
  {
    key: 'homeLead',
    type: 'enum',
    label: (m) => m.siteTemplates.arena.options.homeLead.label,
    help: (m) => m.siteTemplates.arena.options.homeLead.help,
    default: 'auto',
    values: [
      { value: 'auto', label: (m) => m.siteTemplates.arena.options.homeLead.auto },
      { value: 'game', label: (m) => m.siteTemplates.arena.options.homeLead.game },
      { value: 'story', label: (m) => m.siteTemplates.arena.options.homeLead.story },
      { value: 'banner', label: (m) => m.siteTemplates.arena.options.homeLead.banner },
    ],
  },
  {
    key: 'navColor',
    type: 'enum',
    label: (m) => m.siteTemplates.arena.options.navColor.label,
    help: (m) => m.siteTemplates.arena.options.navColor.help,
    default: 'black',
    values: [
      { value: 'black', label: (m) => m.siteTemplates.arena.options.navColor.black },
      { value: 'league', label: (m) => m.siteTemplates.arena.options.navColor.league },
    ],
  },
  {
    key: 'canvas',
    type: 'enum',
    role: 'surface',
    autoMap: { light: 'light', dark: 'night' },
    label: (m) => m.siteTemplates.arena.options.canvas.label,
    help: (m) => m.siteTemplates.arena.options.canvas.help,
    default: 'auto',
    values: [
      { value: 'auto', label: (m) => m.siteTemplates.arena.options.canvas.auto },
      { value: 'light', label: (m) => m.siteTemplates.arena.options.canvas.light },
      { value: 'night', label: (m) => m.siteTemplates.arena.options.canvas.night },
    ],
  },
  {
    key: 'profileColors',
    type: 'enum',
    label: (m) => m.siteTemplates.arena.options.profileColors.label,
    help: (m) => m.siteTemplates.arena.options.profileColors.help,
    default: 'team',
    values: [
      { value: 'team', label: (m) => m.siteTemplates.arena.options.profileColors.team },
      { value: 'league', label: (m) => m.siteTemplates.arena.options.profileColors.league },
    ],
  },
  {
    key: 'homeStandings',
    type: 'enum',
    label: (m) => m.siteTemplates.arena.options.homeStandings.label,
    help: (m) => m.siteTemplates.arena.options.homeStandings.help,
    default: 'full',
    values: [
      { value: 'full', label: (m) => m.siteTemplates.arena.options.homeStandings.full },
      { value: 'top8', label: (m) => m.siteTemplates.arena.options.homeStandings.top8 },
      { value: 'off', label: (m) => m.siteTemplates.arena.options.homeStandings.off },
    ],
  },
] as const satisfies OptionsSchema

/** The resolved option values Arena reads at runtime. */
export type ArenaOptions = OptionsOf<typeof ARENA_OPTIONS>
